import React from "react";
import {
  FiEdit2,
  FiExternalLink,
  FiEye,
  FiFacebook,
  FiGlobe,
  FiImage,
  FiMail,
  FiMapPin,
  FiPhone,
  FiUsers,
} from "react-icons/fi";

import { toAbsolutePublicUrl } from "../../../../services/mediaLibraryServices";

import AdminButton from "../../_shared/ui/AdminButton";
import AdminDrawer from "../../_shared/ui/AdminDrawer";
import AssociationStatusBadge from "./AssociationStatusBadge";

import {
  getGoogleMapsUrl,
  normalizeExternalUrl,
} from "../utils/associationsAdminUtils";

export default function AssociationsPreviewDrawer({
  open,
  item,
  onClose,
  onEditFromPreview,
}) {
  const logoUrl = item?.logo_asso ? toAbsolutePublicUrl(item.logo_asso) : "";
  const mapsUrl = getGoogleMapsUrl(item?.gps_asso);

  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      title="Aperçu de l’association"
      subtitle="Visualisez rapidement la fiche association."
      icon={FiEye}
      size="xl"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <AdminButton variant="secondary" onClick={onClose}>
            Fermer
          </AdminButton>

          {item?.id ? (
            <AdminButton
              icon={FiEdit2}
              variant="primary"
              onClick={onEditFromPreview}
            >
              Modifier cette association
            </AdminButton>
          ) : null}
        </div>
      }
    >
      {!item ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Association introuvable.
        </p>
      ) : (
        <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="bg-gradient-to-br from-sky-50 via-white to-indigo-50 p-5 dark:from-slate-900 dark:via-slate-950 dark:to-sky-950/40 sm:p-7">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
              <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-[1.8rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={item.nom_asso}
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <FiImage className="text-5xl text-slate-300 dark:text-slate-600" />
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <AssociationStatusBadge active={!!item.is_active} />

                  {item.categorie ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300">
                      <FiUsers />
                      {item.categorie}
                    </span>
                  ) : null}
                </div>

                <h1 className="mt-4 text-3xl font-bold leading-tight text-slate-950 dark:text-white">
                  {item.nom_asso}
                </h1>

                {item.contact_asso ? (
                  <p className="mt-2 text-sm font-bold text-slate-500 dark:text-slate-400">
                    {item.contact_asso}
                    {item.fonction_asso ? ` — ${item.fonction_asso}` : ""}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="grid gap-5 p-5 sm:p-7 lg:grid-cols-[1fr_320px]">
            <div>
              {item.description_asso ? (
                <div
                  className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-bold prose-a:text-sky-600"
                  dangerouslySetInnerHTML={{
                    __html: item.description_asso,
                  }}
                />
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Aucune description renseignée.
                </p>
              )}
            </div>

            <aside className="space-y-4">
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-sm font-bold text-slate-950 dark:text-white">
                  Coordonnées
                </p>

                <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                  {item.tel_asso ? (
                    <p className="flex items-center gap-2">
                      <FiPhone />
                      {item.tel_asso}
                    </p>
                  ) : null}

                  {item.mail_asso ? (
                    <p className="flex items-center gap-2">
                      <FiMail />
                      {item.mail_asso}
                    </p>
                  ) : null}

                  {item.adresse_asso ? (
                    <p className="flex items-start gap-2 whitespace-pre-line">
                      <FiMapPin className="mt-1 shrink-0" />
                      {item.adresse_asso}
                    </p>
                  ) : null}

                  {item.gps_asso ? (
                    <p className="flex items-center gap-2">
                      <FiMapPin />
                      {item.gps_asso}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-sm font-bold text-slate-950 dark:text-white">
                  Liens utiles
                </p>

                <div className="mt-3 flex flex-col gap-2">
                  {item.website_asso ? (
                    <a
                      href={normalizeExternalUrl(item.website_asso)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-2xl bg-sky-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-sky-700"
                    >
                      <FiGlobe />
                      Site web
                    </a>
                  ) : null}

                  {item.facebook_asso ? (
                    <a
                      href={normalizeExternalUrl(item.facebook_asso)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-700"
                    >
                      <FiFacebook />
                      Facebook
                    </a>
                  ) : null}

                  {mapsUrl ? (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-2xl bg-amber-500 px-4 py-3 text-sm font-bold text-white transition hover:bg-amber-600"
                    >
                      <FiExternalLink />
                      Ouvrir la carte
                    </a>
                  ) : null}

                  {!item.website_asso && !item.facebook_asso && !mapsUrl ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Aucun lien renseigné.
                    </p>
                  ) : null}
                </div>
              </div>
            </aside>
          </div>
        </article>
      )}
    </AdminDrawer>
  );
}